import React, { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "@/lib/toast";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import Navbar from "@/components/Navbar";
import VerifyIdFlow from "@/components/verify/VerifyIdFlow";
import { ShieldCheck, UserX, Loader2 } from "lucide-react";

// Dedicated ID-verification entry point used by every verify CTA (wave emails, badge-reset
// email, both in-app banners, dashboard card). Emailed links carry an ACCOUNT-BOUND ?token=…
// that resolves to a specific user server-side, so the flow always verifies the RIGHT account
// — never whoever happens to be logged in on the device that opened the link.
export default function VerifyLaunch() {
  const { user, loading, logout } = useAuth();
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const token = params.get("token") || "";

  const [launch, setLaunch] = useState(false);
  const [linkState, setLinkState] = useState(token ? "checking" : "ok"); // checking | ok | mismatch | bad
  const [target, setTarget] = useState(null);

  // Resolve the emailed token (read-only) to learn who the link is for.
  useEffect(() => {
    if (!token) return;
    let cancelled = false;
    api.get(`/identity/verify-link/resolve?token=${encodeURIComponent(token)}`)
      .then(({ data }) => { if (!cancelled) setTarget(data); })
      .catch(() => { if (!cancelled) setTarget({ status: "invalid" }); });
    return () => { cancelled = true; };
  }, [token]);

  useEffect(() => {
    if (loading) return;

    // ----- Token path: bind to a specific account -----
    if (token) {
      if (!target) return; // still resolving
      const st = target.status;
      if (st !== "valid") {
        setLinkState("bad");
        return;
      }
      if (target.already_verified) {
        toast.success("That ID is already verified.");
        navigate("/dashboard", { replace: true });
        return;
      }
      if (!user) {
        // Force login and RESUME on this exact token link afterwards.
        try { localStorage.setItem("os_after_login", `/verify?token=${encodeURIComponent(token)}`); } catch (e) {}
        navigate("/join", { replace: true });
        return;
      }
      if (user.user_id !== target.user_id) {
        setLinkState("mismatch"); // do NOT silently bounce — offer to switch accounts
        return;
      }
      setLinkState("ok");
      setLaunch(true);
      return;
    }

    // ----- No token: in-app CTA. Do NOT auto-launch — a bare /verify is also what every
    // OLD unbound email link points at, and those must never silently start verification for
    // whoever is logged in. The member clicks the explicit button below to start themselves.
    if (!user) {
      try { localStorage.setItem("os_after_login", "/verify"); } catch (e) {}
      navigate("/join", { replace: true });
      return;
    }
    if (user.identity_verified) {
      toast.success("Your ID is already verified.");
      navigate("/dashboard", { replace: true });
      return;
    }
    // intentionally NOT setLaunch(true) — show the manual start button instead.
  }, [user, loading, navigate, token, target]);

  const switchAccount = () => {
    try { localStorage.setItem("os_after_login", `/verify?token=${encodeURIComponent(token)}`); } catch (e) {}
    logout();
    navigate("/join", { replace: true });
  };

  // ---- Account mismatch: the link is for someone else ----
  if (linkState === "mismatch") {
    return (
      <div className="min-h-screen text-foreground" data-testid="verify-mismatch-page">
        <Navbar />
        <div className="max-w-lg mx-auto px-4 py-24 text-center">
          <UserX className="w-8 h-8 text-blaze mx-auto" strokeWidth={1.6} />
          <p className="mt-4 font-mono text-[10px] uppercase tracking-[0.22em] text-muted-foreground">Wrong account</p>
          <h1 className="mt-2 font-heading text-2xl uppercase tracking-tight" data-testid="verify-mismatch-title">This link isn't for this account</h1>
          <p className="mt-3 text-sm text-secondary-foreground font-body max-w-[52ch] mx-auto leading-relaxed">
            This verification link is for <span className="text-foreground font-semibold">@{target?.username}</span>
            {target?.masked_email ? <> ({target.masked_email})</> : null}, but you're signed in as{" "}
            <span className="text-foreground font-semibold">@{user?.username}</span>. To keep your verification
            attached to the right account, sign out and continue as <span className="text-foreground font-semibold">@{target?.username}</span>.
          </p>
          <button onClick={switchAccount} data-testid="verify-switch-account-btn"
            className="mt-6 inline-flex items-center gap-2 bg-olive hover:bg-olive-bright text-white font-heading font-bold uppercase text-sm px-5 py-3 transition-colors">
            Sign out &amp; continue as @{target?.username}
          </button>
          <button onClick={() => navigate("/dashboard", { replace: true })} data-testid="verify-mismatch-cancel"
            className="mt-2 block w-full text-center font-mono uppercase text-[11px] tracking-wide text-muted-foreground hover:text-foreground py-2 transition-colors">
            Not now
          </button>
        </div>
      </div>
    );
  }

  // ---- Invalid / expired / already-used link ----
  if (linkState === "bad") {
    return (
      <div className="min-h-screen text-foreground" data-testid="verify-badlink-page">
        <Navbar />
        <div className="max-w-lg mx-auto px-4 py-24 text-center">
          <ShieldCheck className="w-8 h-8 text-muted-foreground mx-auto" strokeWidth={1.6} />
          <p className="mt-4 font-mono text-[10px] uppercase tracking-[0.22em] text-muted-foreground">Verification link</p>
          <h1 className="mt-2 font-heading text-2xl uppercase tracking-tight">This link has expired</h1>
          <p className="mt-3 text-sm text-secondary-foreground font-body max-w-[48ch] mx-auto leading-relaxed">
            This verification link is no longer valid — it may have expired or already been used. Ask us to send
            a fresh one, or start verification from your dashboard.
          </p>
          <button onClick={() => navigate("/dashboard", { replace: true })} data-testid="verify-badlink-dashboard"
            className="mt-6 inline-flex items-center gap-2 bg-olive hover:bg-olive-bright text-white font-heading font-bold uppercase text-sm px-5 py-3 transition-colors">
            Go to dashboard
          </button>
        </div>
      </div>
    );
  }

  const checking = linkState === "checking";
  const showButton = !loading && !checking && user && !user.identity_verified;

  return (
    <div className="min-h-screen text-foreground" data-testid="verify-launch-page">
      <Navbar />
      <div className="max-w-lg mx-auto px-4 py-24 text-center">
        <ShieldCheck className="w-8 h-8 text-olive-bright mx-auto" strokeWidth={1.6} />
        <p className="mt-4 font-mono text-[10px] uppercase tracking-[0.22em] text-muted-foreground">Identity verification</p>
        <h1 className="mt-2 font-heading text-2xl uppercase tracking-tight">
          {checking ? "Checking your link…" : token ? "Opening the ID check…" : "Verify your ID"}
        </h1>
        <p className="mt-2 text-sm text-secondary-foreground font-body max-w-[46ch] mx-auto">
          {checking
            ? <span className="inline-flex items-center gap-2"><Loader2 className="w-3.5 h-3.5 animate-spin" /> One moment.</span>
            : token
              ? "Takes about two minutes. If the window didn't open, use the button below."
              : "Takes about two minutes on your phone. Start the check when you're ready."}
        </p>
        {showButton && (
          <button onClick={() => setLaunch(true)} data-testid="verify-launch-btn"
            className="mt-6 inline-flex items-center gap-2 bg-olive hover:bg-olive-bright text-white font-heading font-bold uppercase text-sm px-5 py-3 transition-colors">
            Verify my ID
          </button>
        )}
      </div>
      <VerifyIdFlow open={launch} onClose={() => setLaunch(false)} next="/dashboard" token={token} />
    </div>
  );
}
